import { useState } from 'react';
import { useI18n } from '../i18n/i18n.jsx';
import { useTheme } from '../hooks/useTheme.jsx';
import Container from '../components/UI/Container';
import Section from '../components/UI/Section';
import SectionTitle from '../components/UI/SectionTitle';
import ContactFormModal from '../components/UI/ContactFormModal';
import Testimonials from '../components/UI/Testimonials';

const About = () => {
  const { t } = useI18n();
  const { isDark } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const values = [
    {
      key: 'excellence',
      icon: '🏆'
    },
    {
      key: 'integrity',
      icon: '🤝'
    },
    {
      key: 'studentFirst',
      icon: '🎓'
    },
    {
      key: 'globalPerspective',
      icon: '🌍'
    }
  ];

  const steps = [
    { key: 'step1' },
    { key: 'step2' },
    { key: 'step3' },
    { key: 'step4' }
  ];

  return (
    <main>
      {/* Hero Section */}
      <Section className="pt-24 sm:pt-32 pb-16 bg-neutral-light dark:bg-neutral-900/50">
        <Container>
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <div className="w-16 h-1 bg-accent mx-auto mb-6 rounded-full" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-primary dark:text-white mb-6">
              {t('about.title')}
            </h1>
           
          </div>
        </Container>
      </Section>

      {/* Company Info Section */}
      <Section className="bg-neutral-light dark:bg-neutral-900/50">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-primary dark:text-white mb-6">
                {t('about.companyInfo.title')}
              </h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-300 mb-8 leading-relaxed">
                {t('about.companyInfo.greeting')}
              </p>
            </div>

            <div className="prose prose-lg dark:prose-invert max-w-none animate-fade-in-up">
              <p className="text-base md:text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed mb-8">
                {t('about.companyInfo.intro')}
              </p>

              <h3 className="text-2xl font-semibold text-primary dark:text-white mb-6 mt-10">
                {t('about.companyInfo.servicesTitle')}
              </h3>

              <div className="space-y-8">
                {[1, 2, 3, 4, 5].map((num) => (
                  <div
                    key={num}
                    className="p-6 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-accent dark:hover:border-accent transition-all duration-300"
                  >
                    <h4 className="text-xl font-semibold text-primary dark:text-white mb-4">
                      {t(`about.companyInfo.services.${num}.title`)}
                    </h4>
                    <ul className="space-y-2 text-neutral-700 dark:text-neutral-300">
                      {t(`about.companyInfo.services.${num}.items`, { returnObjects: true }).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-accent mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 bg-primary/5 dark:bg-primary/10 rounded-xl border border-primary/20 dark:border-primary/30">
                <h3 className="text-2xl font-semibold text-primary dark:text-white mb-4">
                  {t('about.companyInfo.geographicTitle')}
                </h3>
                <p className="text-base md:text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed mb-4">
                  {t('about.companyInfo.geographicText')}
                </p>
                <p className="text-base md:text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed">
                  {t('about.companyInfo.conclusion')}
                </p>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Co-Founder Section */}
      <Section className="bg-white dark:bg-neutral-900">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-primary dark:text-white mb-6">
                {t('about.cofounder.title')}
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto mb-8 rounded-full" />
            </div>
            
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-full bg-gradient-to-br from-primary to-accent p-1 flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                    <div className="w-full h-full flex items-center justify-center text-5xl text-primary dark:text-accent">
                      <span className="mb-2">👨‍💼</span>
                    </div>
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-primary dark:text-white mb-2">
                    {t('about.cofounder.name')}
                  </h3>
                  <p className="text-lg text-accent dark:text-accent-light font-medium mb-4">
                    {t('about.cofounder.title')}
                  </p>
                  <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                    {t('about.cofounder.bio')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* International Relations Office Section */}
      <Section className="bg-neutral-50 dark:bg-neutral-900/50">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-primary dark:text-white mb-6">
                {t('about.internationalOffice.title')}
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto mb-8 rounded-full" />
              <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto leading-relaxed">
                {t('about.internationalOffice.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t('about.internationalOffice.services', { returnObjects: true }).map((service, index) => (
                <div 
                  key={index}
                  className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700 hover:border-accent dark:hover:border-accent transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent/10 dark:bg-accent/20 flex items-center justify-center text-accent dark:text-accent-light flex-shrink-0 mt-1">
                      <span className="text-lg">
                        {['🌐', '🔄', '🤝', '🌍'][index] || '✨'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary dark:text-white mb-2">
                        {service}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Mission Section */}
      <Section>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-primary dark:text-white mb-4">
                {t('about.mission')}
              </h2>
              <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
                {t('about.missionText')}
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <div className={`p-8 rounded-2xl border-2 ${
                isDark ? 'border-primary-light bg-neutral-800' : 'border-primary bg-white'
              } shadow-xl`}>
                <div className="text-6xl font-serif font-bold text-center py-12 text-primary dark:text-accent">
                  {t('about.experience.years')}
                </div>
                <div className="text-center text-lg font-medium text-primary dark:text-white">
                  {t('about.experience.label')}
                </div>
                <div className="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-300">
                  {t('about.experience.description')}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Values Section */}
      <Section className="bg-neutral-light dark:bg-neutral-900/50">
        <Container>
          <SectionTitle
            title={t('about.values')}
            subtitle={t('about.valuesSubtitle')}
          />
          
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="p-6 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-accent dark:hover:border-accent transition-all duration-300 hover:shadow-lg animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl mb-3" aria-hidden="true">{value.icon}</div>
                <h3 className="text-lg font-semibold text-primary dark:text-white mb-2">
                  {t(`about.${value.key}`)}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t(`about.${value.key}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Our Approach Section */}
      <Section>
        <Container>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-primary dark:text-white mb-8 text-center">
              {t('about.approach.title')}
            </h2>
            <div className="space-y-8">
              {steps.map((item, index) => (
                <div
                  key={index}
                  className="flex gap-6 items-start p-6 rounded-xl bg-neutral-light dark:bg-neutral-900/50 hover:bg-accent/5 dark:hover:bg-accent/10 transition-colors duration-200"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-primary dark:text-white mb-2">
                      {t(`about.approach.${item.key}.title`)}
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                      {t(`about.approach.${item.key}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Final CTA */}
      <Section className="bg-primary text-white pb-24 md:pb-16">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold mb-4">
              {t('about.cta.title')}
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              {t('about.cta.subtitle')}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-block px-8 py-4 bg-white text-primary font-medium rounded-lg hover:bg-neutral-100 transition-colors duration-200 shadow-lg"
            >
              {t('about.cta.button')}
            </button>
          </div>
        </Container>
      </Section>
      
      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        defaultSubject="general"
      />
      
      {/* Bottom padding for mobile nav */}
      <div className="pb-24 md:pb-0"></div>
    </main>
  );
};

export default About;
